import { useEffect, useState } from "react";
import api from "../lib/api";
import { formatApiError } from "../lib/auth";
import { toast } from "sonner";
import BrandHeader from "../components/brand/BrandHeader";
import Footer from "../components/brand/Footer";
import { HERO_IMAGES } from "../lib/brand";
import { CheckCircle } from "@phosphor-icons/react";

export default function PartnerWithUs() {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    business_name: "", branch_name: "", category_id: "", address: "",
    contact_name: "", email: "", phone: "", description: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim()) { toast.error("Business name is required"); return; }
    setSubmitting(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      await api.post("/merchant-applications", payload);
      setSubmitted(true);
      toast.success("Application sent.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <BrandHeader />
      <section className="relative">
        <div className="aspect-[16/8] sm:aspect-[16/5] relative">
          <img src={HERO_IMAGES[0]} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 to-[#050505]" />
        </div>
      </section>
      <section className="max-w-3xl mx-auto px-6 -mt-32 relative">
        <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">For Venues</p>
        <h1 className="mt-3 font-display text-4xl sm:text-5xl text-white leading-tight">Become a Social Circle Partner.</h1>
        <p className="text-white/65 mt-5">Apply to join our private network of bars, clubs, lounges, restaurants and hotels. Once approved, you'll receive a merchant dashboard, a branded scanner PWA, and access to The Circle.</p>

        {submitted ? (
          <div className="tsc-card p-8 mt-10 text-center" data-testid="partner-form-success">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-gold-gradient text-black mb-4">
              <CheckCircle size={28} weight="duotone" />
            </div>
            <p className="text-[10px] tracking-luxe uppercase text-[#D4AF37]">Application received</p>
            <h2 className="font-display text-2xl text-white mt-2">Thank you for applying.</h2>
            <p className="text-white/55 mt-3">Our partnerships team will review your venue and get in touch. We typically respond within 3 business days.</p>
            <button onClick={() => { setSubmitted(false); setForm({ business_name: "", branch_name: "", category_id: "", address: "", contact_name: "", email: "", phone: "", description: "" }); }} className="tsc-btn-outline mt-6" data-testid="partner-form-new">Submit another</button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 grid gap-5" data-testid="partner-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-[10px] tracking-luxe uppercase text-white/60">Business name *</label>
                <input required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="tsc-input mt-1.5" placeholder="Your venue" data-testid="partner-input-business" />
              </div>
              <div>
                <label className="text-[10px] tracking-luxe uppercase text-white/60">Branch name</label>
                <input value={form.branch_name} onChange={(e) => setForm({ ...form, branch_name: e.target.value })} className="tsc-input mt-1.5" placeholder="e.g. BGC Branch" data-testid="partner-input-branch" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-[10px] tracking-luxe uppercase text-white/60">Category</label>
                <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="tsc-input mt-1.5" data-testid="partner-input-category">
                  <option value="">Select…</option>
                  {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] tracking-luxe uppercase text-white/60">Contact name</label>
                <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="tsc-input mt-1.5" placeholder="Owner / Manager" data-testid="partner-input-contact" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="text-[10px] tracking-luxe uppercase text-white/60">Email</label>
                <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="tsc-input mt-1.5" type="email" data-testid="partner-input-email" />
              </div>
              <div>
                <label className="text-[10px] tracking-luxe uppercase text-white/60">Phone</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="tsc-input mt-1.5" data-testid="partner-input-phone" />
              </div>
            </div>
            <div>
              <label className="text-[10px] tracking-luxe uppercase text-white/60">Address</label>
              <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="tsc-input mt-1.5" data-testid="partner-input-address" />
            </div>
            <div>
              <label className="text-[10px] tracking-luxe uppercase text-white/60">Tell us about your venue</label>
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="tsc-input mt-1.5" rows="4" data-testid="partner-input-message" />
            </div>
            <button type="submit" disabled={submitting} className="tsc-btn-gold mt-2 w-full sm:w-auto" data-testid="partner-submit">
              {submitting ? "Sending…" : "Send Application"}
            </button>
          </form>
        )}
      </section>
      <Footer />
    </div>
  );
}
